import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CashDenominationCounter } from '@/components/pos/CashDenominationCounter'
import { CashDrawerDifference } from '@/components/pos/CashDrawerReconciliation'
import {
  CashDrawerCloseWorkflowContent,
  useCashDrawerCloseWorkflow,
} from '@/components/pos/CashDrawerCloseWorkflow'
import { useRotatingIdempotencyKey } from '@/components/pos/use-rotating-idempotency-key'
import { serverFieldErrors, submitSchemaValidator, useAppForm } from '@/lib/form'
import {
  useAdjustCashDrawerMutation,
  useCurrentCashDrawer,
  useStartCashDrawerMutation,
} from '@/lib/services/cash-drawers/cash-drawers.query'
import {
  adjustCashDrawerSchema,
  startCashDrawerSchema,
} from '@/lib/services/cash-drawers/cash-drawers.schema'
import type {
  AdjustCashDrawerInput,
  CashDrawerSession,
  CashDrawerWorkstation,
  CurrentCashDrawer,
  CashDrawerIdInput,
} from '@/lib/services/cash-drawers/cash-drawers.types'
import { formatCurrency, getErrorMessage, parseCurrency } from '@/lib/utils'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export { useRotatingIdempotencyKey } from '@/components/pos/use-rotating-idempotency-key'

export interface WorkstationPairingDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  workstations: CashDrawerWorkstation[]
  workstationId: CashDrawerIdInput
  onPair: (workstationId: number) => unknown
}

export function WorkstationPairingDialog({
  open,
  onOpenChange,
  workstations,
  workstationId,
  onPair,
}: WorkstationPairingDialogProps) {
  const savedWorkstationId = workstationId ? String(workstationId) : ''
  const savedWorkstationIsAvailable = workstations.some(
    (workstation) => String(workstation.id) === savedWorkstationId
  )
  const onlyWorkstation = workstations.length === 1 ? workstations[0] : undefined
  const [selectedWorkstationId, setSelectedWorkstationId] = useState(
    savedWorkstationIsAvailable
      ? String(workstationId)
      : onlyWorkstation
        ? String(onlyWorkstation.id)
        : ''
  )
  const selectedWorkstation =
    workstations.find((workstation) => String(workstation.id) === String(selectedWorkstationId)) ??
    onlyWorkstation ??
    null
  const selectedValue = selectedWorkstation ? String(selectedWorkstation.id) : ''

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Set up this register</DialogTitle>
          <DialogDescription>
            Pair this browser with the physical register beside it. Cash drawer activity and cash
            payments on this device will use only that register.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label>Register for this device</Label>
          <Select value={selectedValue} onValueChange={setSelectedWorkstationId}>
            <SelectTrigger>
              <SelectValue placeholder="Choose a register" />
            </SelectTrigger>
            <SelectContent>
              {workstations.map((workstation) => (
                <SelectItem key={workstation.id} value={String(workstation.id)}>
                  {workstation.name} · {workstation.location.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          disabled={!selectedWorkstation}
          onClick={() => {
            if (selectedWorkstation) onPair(Number(selectedWorkstation.id))
          }}
        >
          Use this register
        </Button>
      </DialogContent>
    </Dialog>
  )
}

export interface StartDrawerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  workstationId: number
}

export function StartDrawerDialog({ open, onOpenChange, workstationId }: StartDrawerDialogProps) {
  const { data, isError, isFetchedAfterMount, isFetching, isLoading, refetch } =
    useCurrentCashDrawer(workstationId, {
      enabled: open,
      refetchOnMount: 'always',
    })

  if (isError) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start cash drawer</DialogTitle>
            <DialogDescription>
              We couldn&apos;t load this cash drawer&apos;s details. Check your connection and try
              again.
            </DialogDescription>
          </DialogHeader>
          <Button type="button" variant="outline" disabled={isFetching} onClick={() => refetch()}>
            {isFetching ? <Loader2 className="size-4 animate-spin" /> : null}
            {isFetching ? 'Trying again...' : 'Try again'}
          </Button>
        </DialogContent>
      </Dialog>
    )
  }

  if (isLoading || !isFetchedAfterMount || !data) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Start cash drawer</DialogTitle>
            <DialogDescription>Loading cash drawer details...</DialogDescription>
          </DialogHeader>
          <Loader2 className="mx-auto size-6 animate-spin text-muted-foreground" />
        </DialogContent>
      </Dialog>
    )
  }

  if (data.session) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cash drawer already started</DialogTitle>
            <DialogDescription>
              This register already has a cash drawer in use. Count it before starting a new one.
            </DialogDescription>
          </DialogHeader>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <StartDrawerFormDialog
      key={`${workstationId}-${data.expected_opening_cash}`}
      open={open}
      onOpenChange={onOpenChange}
      workstationId={workstationId}
      expectedCash={data.expected_opening_cash}
      lastClose={data.last_close}
    />
  )
}

interface StartDrawerFormDialogProps extends StartDrawerDialogProps {
  expectedCash: CurrentCashDrawer['expected_opening_cash']
  lastClose: CurrentCashDrawer['last_close']
}

function StartDrawerFormDialog({
  open,
  onOpenChange,
  workstationId,
  expectedCash,
  lastClose,
}: StartDrawerFormDialogProps) {
  const mutation = useStartCashDrawerMutation()
  const [idempotencyKey, rotateIdempotencyKey] = useRotatingIdempotencyKey()
  const form = useAppForm({
    defaultValues: {
      opening_counted_cash: '',
      note: '',
    },
    validators: {
      // The schema coerces the typed strings to numbers.
      onSubmit: submitSchemaValidator(startCashDrawerSchema),
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          await mutation.mutateAsync({
            cash_drawer_workstation_id: workstationId,
            expected_opening_cash: parseCurrency(expectedCash),
            opening_counted_cash: Number(value.opening_counted_cash),
            note: value.note.trim() || undefined,
            idempotency_key: idempotencyKey,
          })
          rotateIdempotencyKey()
          toast.success('Cash drawer started', {
            description:
              "When you're done for the day, count it from Cash Drawer at the top of Orders or Create Order.",
          })
          formApi.reset()
          onOpenChange(false)
          return undefined
        } catch (error) {
          toast.error('Unable to start the cash drawer', { description: getErrorMessage(error) })
          return serverFieldErrors(error)
        }
      },
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!mutation.isPending || nextOpen) onOpenChange(nextOpen)
      }}
    >
      <DialogContent
        className="@container"
        size="large"
        viewport="scroll"
        showCloseButton={!mutation.isPending}
      >
        <DialogHeader>
          <DialogTitle>Start cash drawer</DialogTitle>
          <DialogDescription>
            We expect {formatCurrency(expectedCash)} from the last count. Enter what is actually in
            the drawer.
          </DialogDescription>
        </DialogHeader>
        <form.AppForm>
          <form.Form className="space-y-4">
            <form.AppField name="opening_counted_cash">
              {(field) => (
                <field.MoneyInput
                  label="Cash in drawer"
                  showFreeBadge={false}
                  formatEmptyAsZero={false}
                />
              )}
            </form.AppField>
            {/* A register's first count is its float: there's nothing to differ from. */}
            {lastClose && (
              <form.Subscribe selector={(state) => state.values.opening_counted_cash}>
                {(cash) => (
                  <CashDrawerDifference
                    label="Starting difference"
                    counted={cash}
                    expected={expectedCash}
                  />
                )}
              </form.Subscribe>
            )}
            <CashDenominationCounter
              onChange={(amount: string) => form.setFieldValue('opening_counted_cash', amount)}
            />
            <form.AppField name="note">
              {(field) => <field.TextInput label="Note (optional)" />}
            </form.AppField>
            <form.SubmitButton mutation={mutation}>Start cash drawer</form.SubmitButton>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  )
}

export interface AdjustDrawerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  session: CashDrawerSession
}

interface AdjustDrawerFormValues {
  direction: AdjustCashDrawerInput['direction']
  amount: string
  reason: string
}

export function AdjustDrawerDialog({ open, onOpenChange, session }: AdjustDrawerDialogProps) {
  const mutation = useAdjustCashDrawerMutation()
  const [idempotencyKey, rotateIdempotencyKey] = useRotatingIdempotencyKey()
  const defaultValues: AdjustDrawerFormValues = { direction: 'in', amount: '', reason: '' }
  const form = useAppForm({
    defaultValues,
    validators: {
      // The schema coerces the typed strings to numbers.
      onSubmit: submitSchemaValidator(adjustCashDrawerSchema),
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          await mutation.mutateAsync({
            sessionId: session.id,
            direction: value.direction,
            amount: Number(value.amount),
            reason: value.reason.trim(),
            idempotency_key: idempotencyKey,
          })
          rotateIdempotencyKey()
          toast.success(value.direction === 'in' ? 'Cash added' : 'Cash removed')
          formApi.reset()
          onOpenChange(false)
          return undefined
        } catch (error) {
          toast.error('Unable to record this adjustment', {
            description: getErrorMessage(error),
          })
          return serverFieldErrors(error)
        }
      },
    },
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!mutation.isPending || nextOpen) onOpenChange(nextOpen)
      }}
    >
      <DialogContent showCloseButton={!mutation.isPending}>
        <DialogHeader>
          <DialogTitle>Add or remove cash</DialogTitle>
          <DialogDescription>
            Record non-sale cash movement so the expected total stays accurate.
          </DialogDescription>
        </DialogHeader>
        <form.AppForm>
          <form.Form className="space-y-4">
            <form.AppField name="direction">
              {(field) => (
                <field.RadioButtonGroup
                  label="Direction"
                  appearance="segmented"
                  classNames={{ container: 'grid grid-cols-2', button: 'justify-center' }}
                  disabled={mutation.isPending}
                  joined
                  options={[
                    { id: 'in', name: 'Add cash', selectedTone: 'success' },
                    { id: 'out', name: 'Remove cash', selectedTone: 'destructive' },
                  ]}
                  size="sm"
                  unselectedVariant="outline"
                />
              )}
            </form.AppField>
            <form.AppField name="amount">
              {(field) => (
                <field.MoneyInput label="Amount" showFreeBadge={false} formatEmptyAsZero={false} />
              )}
            </form.AppField>
            <form.AppField name="reason">
              {(field) => <field.TextInput label="Reason" />}
            </form.AppField>
            <form.SubmitButton mutation={mutation}>Record adjustment</form.SubmitButton>
          </form.Form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  )
}

export interface CloseDrawerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  session: CashDrawerSession
}

export function CloseDrawerDialog({ open, onOpenChange, session }: CloseDrawerDialogProps) {
  const workflow = useCashDrawerCloseWorkflow({
    session,
    onSuccess: () => onOpenChange(false),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!workflow.mutation.isPending || nextOpen) onOpenChange(nextOpen)
      }}
    >
      <DialogContent
        className="@container"
        size="xlarge"
        viewport="scroll"
        showCloseButton={!workflow.mutation.isPending}
      >
        <DialogHeader>
          <DialogTitle>Count this drawer</DialogTitle>
          <DialogDescription>
            Count what is here, choose what stays in the drawer, and record the deposit.
          </DialogDescription>
        </DialogHeader>
        <CashDrawerCloseWorkflowContent session={session} {...workflow} />
      </DialogContent>
    </Dialog>
  )
}
